import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DetailsResponse } from 'gql/autogen/DetailsResponse';
import { ApiBaseState, DetailsRequestProps, GqlApiResponse } from 'store/api/api.types';
import { updateApiBaseState } from 'store/api/apiUtils';
import { resetStateWithDefaultApiBaseState } from 'store/api/activitiesGqlApi';
import { measureDetailApiResponse } from 'store/api/measurements/detailGqlMeasurement';
import { defaultExpandedAccordion, ExpandedAccordionDict, NavigationHashEnum } from 'core/type/Details.types';
import { DefaultApiBaseState } from 'store/api/ApiConstants';

export interface DetailsState extends ApiBaseState {
    detailsResponse?: DetailsResponse;
    expandedAccordion?: ExpandedAccordionDict;
    expandedModularAccordion?: Record<string, boolean>;
    selectedTab?: string;
    isScrolling?: boolean;
    // TODO: Make translationState mandatory by using 'detailsInitialState', this refactor requires changing 20+ files. same as 'src/component/page/search/store/search.ts'
    reviewTranslationState?: { isLoading: boolean; index: number };
    activityToken?: string;
    isExpandFirstOfferCardInitiated?: boolean;
}

export const detailsInitialState: DetailsState = {
    ...DefaultApiBaseState,
    expandedAccordion: defaultExpandedAccordion, // TODO: Remove this after integrate sim card detail page ACT-5340
    expandedModularAccordion: {},
    selectedTab: NavigationHashEnum.OverviewSection,
    reviewTranslationState: { isLoading: false, index: -1 },
    isScrolling: false,
    isExpandFirstOfferCardInitiated: false,
};

let detailInitialStateLazy: DetailsState;

const detailSlice = createSlice({
    name: 'detail',
    initialState: (): DetailsState => {
        // Intentional: Somehow webpack is unable to load variable DefaultApiBaseState in this file. This implementation is a lazy initialization implementation.
        if (!detailInitialStateLazy) {
            detailInitialStateLazy = { ...detailsInitialState, ...DefaultApiBaseState };
        }
        return detailInitialStateLazy;
    },
    reducers: {
        detailsInitialState: () => detailsInitialState,
        resetDetailsState: (state) => {
            resetStateWithDefaultApiBaseState(state);
            state.detailsResponse = undefined;
            state.expandedAccordion = defaultExpandedAccordion;
            state.expandedModularAccordion = {};
            state.selectedTab = NavigationHashEnum.OverviewSection;
            state.activityToken = undefined;
        },
        detailsLoadFinished: (state, action: PayloadAction<GqlApiResponse<DetailsResponse, DetailsRequestProps>>) => {
            state.detailsResponse = action.payload.result;
            updateApiBaseState(state, action);
            state.activityToken = state.detailsResponse?.result?.activity?.activityRepresentativeInfo?.activityToken;
            if (state.isError || state.isSuccess) {
                measureDetailApiResponse(
                    state.isSuccess,
                    action.payload.pageTypeId,
                    action.payload.config,
                    state.detailsResponse?.result?.activity,
                    action.payload.props.activityId,
                    state.error,
                );
            }
        },
        setExpandedAccordionOnDetailsPage: (state, action: PayloadAction<{ selector: string; status: boolean }>) => {
            // TODO: Remove this after integrate sim card detail page ACT-5340
            if (state.expandedAccordion) {
                state.expandedAccordion[action.payload.selector] = action.payload.status;
            }
            if (state.expandedModularAccordion) {
                state.expandedModularAccordion[action.payload.selector] = action.payload.status;
            }
        },
        setSelectedTabOnDetailsPage: (state, action: PayloadAction<string>) => {
            state.selectedTab = action.payload;
        },
        setIsScrolling: (state, action: PayloadAction<boolean>) => {
            // We will use this flag to prevent focus in order to not stop the scrolling
            state.isScrolling = action.payload;
        },
        setReviewTranslationState: (state, action: PayloadAction<{ isLoading: boolean; index: number }>) => {
            state.reviewTranslationState = action.payload;
        },
        setIsExpandFirstOfferCardInitiated: (state, action: PayloadAction<{ expandFirstCardOnLanding: boolean }>) => {
            state.isExpandFirstOfferCardInitiated = action.payload.expandFirstCardOnLanding;
        },
    },
});

export const {
    resetDetailsState,
    detailsLoadFinished,
    setExpandedAccordionOnDetailsPage,
    setSelectedTabOnDetailsPage,
    setIsScrolling,
    setReviewTranslationState,
    setIsExpandFirstOfferCardInitiated,
} = detailSlice.actions;
export default detailSlice.reducer;
